import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  Container,
} from '@mui/material';
import { MarkEmailRead } from '@mui/icons-material';
import { auth } from '../config/firebase';
import api from '../utils/api';
import { useAuth } from '../auth/useAuth';

type ResendState = 'idle' | 'sending' | 'sent' | 'error';

/**
 * The "verify your email" wall. Shown to an authenticated user whose email is
 * not yet verified (gated in App.tsx). Offers resend and logout — clicking the
 * link in the verification email routes the user on automatically (VerifyEmail),
 * so no manual re-check is needed here. All app access is blocked until
 * verification completes.
 */
const VerifyEmailWall: React.FC = () => {
  const { user, logout } = useAuth();
  const email = user?.email || auth.currentUser?.email || '';
  const [resendState, setResendState] = useState<ResendState>('idle');
  const [message, setMessage] = useState('');

  const resend = async () => {
    setResendState('sending');
    setMessage('');
    try {
      await api.post('/public/resend-verification');
      setResendState('sent');
    } catch (e: any) {
      setResendState('error');
      setMessage(
        !navigator.onLine || String(e?.message || '').includes('Network')
          ? 'You appear to be offline. Reconnect to send the verification email.'
          : 'Could not send the email right now. Please try again in a moment.',
      );
    }
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box sx={{ marginTop: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Box
          component="img"
          src="/StableTrack.png"
          alt="StableTrack"
          sx={{ height: 120, width: 120, mb: 3 }}
        />
        <Card sx={{ width: '100%', boxShadow: 3 }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <MarkEmailRead sx={{ fontSize: 60, color: '#C9772B', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Verify your email
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              We sent a verification link to{' '}
              {email ? <strong>{email}</strong> : 'your email address'}. Click it to activate your
              StableTrack account.
            </Typography>

            {resendState === 'sent' && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Verification email sent. Check your inbox (and spam folder).
              </Alert>
            )}
            {message && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {message}
              </Alert>
            )}

            <Button
              fullWidth
              variant="contained"
              onClick={resend}
              disabled={resendState === 'sending'}
              sx={{ mb: 2, bgcolor: '#C9772B', '&:hover': { bgcolor: '#6B3410' } }}
            >
              {resendState === 'sending' ? 'Sending…' : 'Resend verification email'}
            </Button>
            <Button
              variant="text"
              onClick={logout}
              sx={{ color: 'text.secondary', textTransform: 'none' }}
            >
              Log out
            </Button>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default VerifyEmailWall;
